import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

interface Props {
  translationPrefix: string;
}

const SolutionIntegrationsI18n = ({ translationPrefix }: Props) => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject } = useLanguage();
  const items = tObject<Array<{ name: string; description: string; category: string; comingSoon?: boolean }>>(`${translationPrefix}.integrations.items`);

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={cn("py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center"><Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t(`${translationPrefix}.integrations.badge`)}</Badge></div>
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6 text-center">{t(`${translationPrefix}.integrations.title`)}</h2>
          <p className="text-lg text-muted-foreground text-center mb-12 max-w-3xl mx-auto">{t(`${translationPrefix}.integrations.intro`)}</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((integration, index) => (
              <div key={index} className={cn("bg-background rounded-xl p-5 border border-border transition-all duration-500 hover:shadow-md hover:border-chathero-mint/30", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")} style={{ transitionDelay: isVisible ? `${index * 75}ms` : "0ms" }}>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-chathero-mint/10 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-chathero-mint" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{integration.name}</h3>
                      {integration.comingSoon && (
                        <span className="text-xs bg-chathero-orange/20 text-chathero-orange px-2 py-0.5 rounded-full">coming soon</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{integration.description}</p>
                    <span className="text-xs text-muted-foreground/70 mt-1 block">{integration.category}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionIntegrationsI18n;
